import { useEffect, useRef, useCallback } from 'react';

// Hook per monitoraggio performance
export const usePerformanceMonitor = (componentName: string) => {
  const renderCountRef = useRef(0);
  const mountTimeRef = useRef<number>();
  const lastRenderTimeRef = useRef<number>();

  useEffect(() => {
    mountTimeRef.current = performance.now();
    renderCountRef.current += 1;

    // Log performance metrics in development
    if (process.env.NODE_ENV === 'development') {
      const now = performance.now();
      const timeSinceMount = mountTimeRef.current ? now - mountTimeRef.current : 0;
      const timeSinceLastRender = lastRenderTimeRef.current ? now - lastRenderTimeRef.current : 0;

      // console.log(`[${componentName}] Render #${renderCountRef.current}`, {
      //   timeSinceMount: `${timeSinceMount.toFixed(2)}ms`,
      //   timeSinceLastRender: `${timeSinceLastRender.toFixed(2)}ms`,
      //   totalRenders: renderCountRef.current
      // });

      lastRenderTimeRef.current = now;
    }

    // Monitor memory usage
    if ('memory' in performance) {
      const memInfo = (performance as any).memory;
      if (renderCountRef.current % 10 === 0) { // Log ogni 10 render
        // console.log(`[${componentName}] Memory:`, {
        //   used: `${(memInfo.usedJSHeapSize / 1024 / 1024).toFixed(2)}MB`,
        //   total: `${(memInfo.totalJSHeapSize / 1024 / 1024).toFixed(2)}MB`,
        //   limit: `${(memInfo.jsHeapSizeLimit / 1024 / 1024).toFixed(2)}MB`
        // });
      }
    }
  });

  const logPerformance = useCallback((action: string, data?: any) => {
    if (process.env.NODE_ENV === 'development') {
      // console.log(`[${componentName}] ${action}:`, data);
    }
  }, [componentName]);

  return { logPerformance };
};

// Hook per monitoraggio Core Web Vitals
export const useWebVitals = () => {
  useEffect(() => {
    // Monitor CLS (Cumulative Layout Shift)
    let clsValue = 0;
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (!(entry as any).hadRecentInput) {
          clsValue += (entry as any).value;
        }
      }
    });
    observer.observe({ entryTypes: ['layout-shift'] });

    // Monitor LCP (Largest Contentful Paint)
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      // console.log('LCP:', lastEntry.startTime);
    });
    lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

    // Monitor FID (First Input Delay)
    const fidObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        // console.log('FID:', (entry as any).processingStart - entry.startTime);
      }
    });
    fidObserver.observe({ entryTypes: ['first-input'] });

    return () => {
      observer.disconnect();
      lcpObserver.disconnect();
      fidObserver.disconnect();
    };
  }, []);
};

// Hook per monitoraggio bundle size e caricamento
export const useBundleMonitor = () => {
  useEffect(() => {
    // Monitor resource loading
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType === 'resource') {
          const resourceEntry = entry as PerformanceResourceTiming;
          if (resourceEntry.name.includes('.js') || resourceEntry.name.includes('.css')) {
            // console.log('Resource loaded:', {
            //   name: resourceEntry.name,
            //   duration: resourceEntry.responseEnd - resourceEntry.requestStart,
            //   size: resourceEntry.transferSize
            // });
          }
        }
      }
    });

    observer.observe({ entryTypes: ['resource'] });

    // Monitor navigation timing
    const navObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType === 'navigation') {
          const navEntry = entry as PerformanceNavigationTiming;
          // console.log('Navigation timing:', {
          //   domContentLoaded: navEntry.domContentLoadedEventEnd - navEntry.domContentLoadedEventStart,
          //   loadComplete: navEntry.loadEventEnd - navEntry.loadEventStart,
          //   totalTime: navEntry.loadEventEnd - navEntry.fetchStart
          // });
        }
      }
    });

    navObserver.observe({ entryTypes: ['navigation'] });

    return () => {
      observer.disconnect();
      navObserver.disconnect();
    };
  }, []);
};

// Utility per misurazione performance di funzioni
export const measurePerformance = async <T>(
  name: string,
  fn: () => Promise<T> | T
): Promise<T> => {
  const start = performance.now();
  try {
    const result = await fn();
    const end = performance.now();
    // console.log(`[${name}] completed in ${(end - start).toFixed(2)}ms`);
    return result;
  } catch (error) {
    const end = performance.now();
    console.error(`[${name}] failed after ${(end - start).toFixed(2)}ms:`, error);
    throw error;
  }
};

// Hook per ottimizzazione re-render con deep comparison
export const useDeepMemo = <T>(
  factory: () => T,
  deps: React.DependencyList
): T => {
  const prevDepsRef = useRef<React.DependencyList>();
  const valueRef = useRef<T>();

  const depsChanged = !prevDepsRef.current ||
    deps.length !== prevDepsRef.current.length ||
    deps.some((dep, index) => !Object.is(dep, prevDepsRef.current![index]));

  if (depsChanged) {
    valueRef.current = factory();
    prevDepsRef.current = deps;
  }

  return valueRef.current!;
};